import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { UpsertSubsidiary } from "../../http/subsidiaries/types";
import SubsidiaryVatCodes from "../subsidiaryVatCodes/SubsidiaryVatCodes";
import Button from "../ui/Button";
import Header from "../ui/Header";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import SubsidiaryDefaultTasks, { SubsidiaryCustomTasksHandle } from "./SubsidiaryCustomTasks";
import SubsidiaryForm, { SubsidiaryFormBody } from "./SubsidiaryForm";
import UserSubsidiaries from "./userSubsidiary/UserSubsidiaries";

const EditSubsidiary: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.t.subsidiaries.edit} />
      <TabsProvider>
        <TabbedEditSubsidiary />
      </TabsProvider>
    </>
  );
};

export default EditSubsidiary;

const TabbedEditSubsidiary: FC = () => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const subsidiariesApi = useSubsidiariesApi();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectUrl = params.get("redirect_uri");
  const aborter = useRef(new AbortController());
  const defaultTasksHandlerRef = useRef<SubsidiaryCustomTasksHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subsidiarySwr = subsidiariesApi.getAdminSubsidiary(id!, aborter.current.signal);
  const [subsidiary, isInitialized] = useDataInitializer(subsidiarySwr.data, subsidiarySwr.error);

  useEffect(() => {
    id && subsidiarySwr.trigger();
  }, [id]);

  useEffect(() => {
    subsidiarySwr.error && dispatch(showErrorNotification(i18n, subsidiarySwr.error));
  }, [subsidiarySwr.error]);

  const onSubmit = async (formData: SubsidiaryFormBody): Promise<void> => {
    if (subsidiary?.id) {
      const body: UpsertSubsidiary = {
        name: formData.name,
        allowMultiPlates: formData.allowMultiPlates,
        vatDeductionCode: formData.vatDeductionCode,
        vatServiceCode: formData.vatServiceCode,
        countryIsoCodeAlpha2: formData.countryIsoCodeAlpha2,
        defaultLanguage: formData.defaultLanguage?.value,
        currentVersionNumber: subsidiary.versionNumber,
        semiramisOrganizationCode: formData.semiramisOrganizationCode,
      };

      try {
        setIsSubmitting(true);
        await subsidiariesApi.updateAdminSubsidiary(subsidiary.id, body);
        subsidiarySwr.trigger();
        dispatch(showSuccessNotification(i18n.t));
        defaultTasksHandlerRef.current?.notifyEdit();
        redirectUrl && navigate(redirectUrl);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabs = [{ title: i18n.t.common.edit }];

  const tabComponents: TabContent[] = [
    {
      body: (
        <SubsidiaryForm
          onSubmit={onSubmit}
          subsidiary={subsidiary}
          isLoading={!isInitialized}
          isDisabled={!userSettings.permissions.subsidiaries.update || isSubmitting || !subsidiary?.id}
          secondaryButton={
            redirectUrl ? (
              <Button buttonProps={{ onClick: () => navigate(redirectUrl) }} type="secondary">
                {i18n.t.common.back}
              </Button>
            ) : undefined
          }
        />
      ),
      eager: true,
      withBorder: true,
    },
  ];

  if (userSettings.permissions.subsidiaryVatCodes.read) {
    tabs.push({ title: i18n.t.common.vatCodes });
    tabComponents.push({ body: <SubsidiaryVatCodes />, withBorder: true });
  }

  tabs.push({ title: i18n.t.defaultTasks.plural });
  tabComponents.push({ body: <SubsidiaryDefaultTasks ref={defaultTasksHandlerRef} />, withBorder: true });

  if (userSettings.permissions.users.read) {
    tabs.push({ title: i18n.t.users.plural });
    tabComponents.push({ body: <UserSubsidiaries />, withBorder: true });
  }

  return (
    <>
      <Tabs tabs={tabs} />
      <TabsContents contents={tabComponents} />
    </>
  );
};
